import { useAtomValue, useSetAtom } from "jotai";
import {
    AccountPanelViewAtom,
    selectedAccountAtom,
} from "@/features/Labeling/Labeling.state";
import {
    AccountPanelView,
    LabelListStatementType,
} from "@/features/Labeling/Labeling.types";
import { RawInput } from "@/ui";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
    AccountCategory,
    AccountLabel,
} from "@/entities/DataAssociation/Account.model";
import { StatementTypeMenu } from "../StatementTypeMenu";
import { labelsByStatementType } from "../../constants/labelsByStatementType";
import { LabelCategoryMenu } from "../LabelCategoryMenu";
import { ACCOUNT_LIST } from "../../constants/labeling.constants";
import { LabelList } from "../LabelList";
import clsx from "clsx";
import { useRowSelection } from "../../hooks/useRowSelection";
import {
    AccountHintType,
    DraftAccount,
    DraftRowEdit,
} from "@/entities/DataAssociation/Draft.model";
import { getAccounts } from "@/entities/DataAssociation/DataAssociation.queries";
import { useApiQuery } from "@/api/hooks/useApiQuery";

export function NewAccount({
    statementType,
    setStatementType,
    hint,
    newAccounts,
}: {
    statementType: LabelListStatementType;
    setStatementType: (statementType: LabelListStatementType) => void;
    hint?: DraftAccount;
    newAccounts: Record<string, DraftRowEdit>;
}) {
    const setView = useSetAtom(AccountPanelViewAtom);
    const { onAccountSelection } = useRowSelection();
    const selectedRow = useAtomValue(selectedAccountAtom);
    const [accountName, setAccountName] = useState<string | null>(
        selectedRow?.rowHeader || null,
    );
    const [selectedLabel, setAccountLabel] = useState<AccountLabel | null>(
        hint?.label ?? null,
    );

    useEffect(() => {
        setAccountName(selectedRow?.rowHeader || null);
    }, [selectedRow?.rowHeader]);

    const categories = Object.keys(
        labelsByStatementType[statementType],
    ) as AccountCategory[];
    const { data: accounts } = useApiQuery(getAccounts);

    const onAccountCreation = useCallback(() => {
        if (!accountName || !selectedLabel) return;
        onAccountSelection({
            type: AccountHintType.NEW,
            accountName: accountName || "",
            label: selectedLabel,
            accountId: undefined,
        });
        setView(AccountPanelView.ACCOUNT_LIST);
    }, [accountName, selectedLabel, onAccountSelection]);

    const [isNameInUse, rowsInUse] = useMemo((): [boolean, string[]] => {
        const rowsWithName = Object.entries(newAccounts || {})
            .filter(
                ([key, row]) =>
                    row.account?.accountName === accountName &&
                    key !== selectedRow?.rowIndex,
            )
            .map(([key]) => String(Number(key) + 1));

        const existingAccountMatch =
            accounts &&
            accounts.some((account) => account.accountName === accountName);

        const isInUse = rowsWithName.length > 0 || !!existingAccountMatch;

        return [isInUse, rowsWithName];
    }, [newAccounts, accountName, accounts, selectedRow?.rowIndex]);

    return (
        <>
            <RawInput
                name="accountName"
                label="Account Name"
                showLabel={false}
                value={accountName || ""}
                showErrorRing={isNameInUse}
                onChange={(e) => setAccountName(e.target.value)}
            />
            {isNameInUse && (
                <p className="ui-element mb-6 mt-1 text-red-600">
                    {rowsInUse.length > 0
                        ? `Account name already in use in row${rowsInUse.length !== 1 ? "s" : ""} ${rowsInUse.join(", ")}`
                        : "Account name already in use"}
                </p>
            )}
            <>
                <div className="mt-2">
                    <StatementTypeMenu
                        statementType={statementType}
                        setStatementType={setStatementType}
                    />
                </div>
                <LabelCategoryMenu
                    categories={categories}
                    initalCategory={
                        hint?.label
                            ? ACCOUNT_LIST[hint.label].category
                            : undefined
                    }
                >
                    {(props) => (
                        <LabelList
                            className="pb-24"
                            labels={Object.values(ACCOUNT_LIST)
                                .filter(
                                    (label) =>
                                        label.category ===
                                        props.selectedCategory,
                                )
                                .sort((a, b) => {
                                    if (hint?.label) {
                                        if (a.code === hint.label) return -1;
                                        if (b.code === hint.label) return 1;
                                    }
                                    return a.name.localeCompare(b.name);
                                })}
                            onLabelSelection={setAccountLabel}
                            selectedLabel={selectedLabel}
                            recommendedLabels={hint?.label ? [hint.label] : []}
                        />
                    )}
                </LabelCategoryMenu>
            </>
            <div
                className={clsx(
                    "bg-default w-full p-6",
                    "absolute inset-x-0 bottom-0",
                )}
            >
                <button
                    className="primary xl w-full"
                    disabled={!selectedLabel || !accountName || isNameInUse}
                    onClick={onAccountCreation}
                >
                    Create account
                </button>
            </div>
        </>
    );
}
